/**
 * PromptRow — one collapsed/expanded prompt row (46 px, C16).
 *
 * Shows: axis dot, text, language flag, level badge (referral), classification
 * status. Expanded: mounts PromptRowExpanded inline.
 *
 * @see B-popup-frontend.md Files and props § PromptRow / PromptRowExpanded
 */
import { Box, Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BrandQuery, BrandStrategy, FirstRunNotice, TargetZone } from '@traaker/shared';
import { effectiveZoneMeta, placeKey, zoneDisplayLabel } from '@traaker/shared';
import type { Family, PromptAllowanceFigures } from './promptStudioModel';
import {
  AXIS_COLOR,
  promptsCount,
  familyShape,
  lastRunOf,
  lastRunLabel,
} from './promptStudioModel';
import type { PromptLastRun } from '@traaker/shared';
import { PromptRowExpanded } from './PromptRowExpanded';
import { familyLanguages } from './variantModel';
import { Flag } from '../../pages/dashboard/Flag';
import { languageFlag } from '../../pages/dashboard/geoFlags';

// ---------------------------------------------------------------------------
// LastRunCell
// ---------------------------------------------------------------------------

interface LastRunCellProps {
  run: { okDay?: string; notToday: string[]; n: number } | null;
  paused?: boolean;
}

export function LastRunCell({ run, paused }: LastRunCellProps) {
  if (!run || !run.okDay) return <Box sx={{ width: 72, minWidth: 72 }} />;

  const todayUtc = new Date().toISOString().slice(0, 10);
  const { key, date } = lastRunLabel(run.okDay, todayUtc);
  // i18n: promptStudio.lastRun.today / .yesterday
  const text = key === 'today' ? "aujourd'hui" : key === 'yesterday' ? 'hier' : (date ?? run.okDay);

  const hasEngineWarning = !paused && run.notToday.length > 0;
  const tooltipLines = hasEngineWarning
    ? [`Passage : ${text}`, `Pas encore aujourd'hui : ${run.notToday.join(', ')}`, run.n > 1 ? `Sur les ${run.n} prompts` : '']
    : [];
  const tooltipText = tooltipLines.filter(Boolean).join('\n');

  return (
    <Tooltip title={tooltipText} arrow>
      <Box
        sx={{
          width: 72,
          minWidth: 72,
          textAlign: 'right',
          fontSize: 11.5,
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
          pr: 0.5,
          cursor: tooltipText ? 'help' : 'default',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// RowTags
// ---------------------------------------------------------------------------

interface RowTagsProps {
  tags?: string[];
  q?: string;
  onTag?: (tag: string) => void;
}

function RowTags({ tags = [], q = '', onTag }: RowTagsProps) {
  const visible = tags.slice(0, 2);
  const rest = tags.length - 2;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
      {visible.map((t) => (
        <Chip
          key={t}
          label={t}
          size="small"
          onClick={onTag ? (e) => { e.stopPropagation(); onTag(t); } : undefined}
          sx={{
            height: 20,
            fontSize: 11,
            bgcolor: q && t.toLowerCase().includes(q.toLowerCase()) ? 'rgba(124,58,237,.12)' : 'rgba(0,0,0,.06)',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      ))}
      {rest > 0 && (
        <Chip
          label={`+${rest}`}
          size="small"
          sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(0,0,0,.04)', '& .MuiChip-label': { px: 1 } }}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// rowPlaceCell
// ---------------------------------------------------------------------------

/**
 * UI-SPEC §2.4 Lieu cell: the root's place (else the first active row's), `more` = the family's
 * other distinct active places (`+N`); under a Lieu filter, the first PASSING place and the other
 * passing places only. `key` = placeKey, the Lieu facet's key.
 */
export function rowPlaceCell(
  f: Family,
  locations: readonly import('@traaker/shared').Location[],
  zoneFilter?: string[],
): { key: string; row: BrandQuery; more: number } {
  const active = f.rows.filter((r) => r.status === 'active');
  const byKey = new Map<string, BrandQuery>();
  for (const r of active.length > 0 ? active : [f.root]) {
    const k = placeKey(r, locations);
    if (!byKey.has(k)) byKey.set(k, r);
  }
  let keys = [...byKey.keys()];
  if (zoneFilter?.length) {
    const passing = keys.filter((k) => zoneFilter.includes(k));
    if (passing.length > 0) keys = passing;
  }
  const rootKey = placeKey(f.root, locations);
  const key = keys.includes(rootKey) ? rootKey : keys[0];
  return { key, row: byKey.get(key) ?? f.root, more: keys.length - 1 };
}

// ---------------------------------------------------------------------------
// LocChip
// ---------------------------------------------------------------------------

interface LocChipProps {
  zone: TargetZone | null;
  legacyZone?: string;
  withWord?: boolean;
  onClick?: () => void;
}

export function LocChip({ zone, legacyZone, withWord, onClick }: LocChipProps) {
  let label: string;
  let flag: string = '🌐';

  if (zone) {
    const { countryCode } = zone;
    // C1 short name; an address reads « street (city) »
    label = zoneDisplayLabel(zone);
    if (countryCode) {
      flag = countryCode.toUpperCase().replace(/./g, (c) =>
        String.fromCodePoint(c.charCodeAt(0) + 127397),
      );
    }
  } else if (legacyZone) {
    label = legacyZone;
    flag = '📍';
  } else {
    label = 'Mondial'; // i18n: promptStudio.place.mondial
    flag = '🌐';
  }

  return (
    <Box
      component={onClick ? 'button' : 'span'}
      onClick={onClick}
      title={withWord ? undefined : label}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 12,
        color: 'text.secondary',
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        border: onClick ? '1px solid' : 'none',
        borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: onClick ? 'transparent' : 'none',
        '&:hover': onClick ? { bgcolor: 'rgba(0,0,0,.04)' } : {},
        flexShrink: 0,
      }}
    >
      <span>{flag}</span>
      <span>{label}</span>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PromptRow
// ---------------------------------------------------------------------------

export interface PromptRowProps {
  family: Family;
  open: boolean;
  q?: string;
  lastRun: ReturnType<typeof lastRunOf>;
  /** locations for effectiveZoneMeta */
  locations?: import('@traaker/shared').Location[];
  onOpen: (id: string | null) => void;
  onTag?: (tag: string) => void;
  onDisable?: (family: Family) => void;
  onDelete?: (family: Family) => void;
  onRetryClassify?: (queryId: string) => void;
  /** Runs record for LastRunCell */
  runs: Record<string, PromptLastRun> | null;
  /** Brand id — passed to PromptRowExpanded for API calls. */
  brandId?: string;
  /** Place search language (brand working language), passed to PromptRowExpanded. */
  lang?: string;
  /** The brand's vocabularies, for the opened prompt's theme / criteria chips. */
  strategy?: BrandStrategy;
  /** Called after a successful Désactiver / Supprimer / save. */
  onChanged?: () => void;
  /** Passed to PromptRowExpanded (lot D: cost line, translate pre-check, first-run strip). */
  promptFigures?: PromptAllowanceFigures | null;
  isAgency?: boolean;
  onFirstRun?: (firstRun?: FirstRunNotice[]) => void;
  /** The list's Lieu filter (placeKeys): the cell shows the first passing place (UI-SPEC §2.4). */
  zoneFilter?: string[];
  /** Batch selection: the row's checkbox is shown only when `onSelect` is set (active rows). */
  selected?: boolean;
  onSelect?: (rootId: string) => void;
}

export function PromptRow({
  family,
  open,
  q = '',
  lastRun,
  locations = [],
  onOpen,
  onTag,
  onDisable: _onDisable,
  onDelete: _onDelete,
  onRetryClassify: _onRetryClassify,
  runs: _runs,
  brandId,
  lang,
  strategy,
  onChanged,
  promptFigures,
  isAgency,
  onFirstRun,
  selected = false,
  onSelect,
  zoneFilter,
}: PromptRowProps) {
  const { i18n } = useTranslation();
  const { root } = family;
  const isPending = root.axis === 'pending';
  const count = promptsCount(family);
  // UI-SPEC §2.4: a row with no `language` shows no flag here (no defaulting: '' then dropped).
  const langs = familyLanguages(family, '').filter(Boolean).map((code) => ({ code, name: languageName(code, i18n.language) }));
  const shape = familyShape(family, locations);
  const showFamilyToken = shape.langs * shape.places > 1;

  const axisColor = !isPending && root.axis !== 'pending'
    ? AXIS_COLOR[root.axis as keyof typeof AXIS_COLOR] ?? '#888'
    : '#f59e0b'; // amber for pending

  const place = rowPlaceCell(family, locations, zoneFilter);
  const effectiveZone = effectiveZoneMeta(place.row, locations);

  // Highlight search match in text
  const textNode = q && root.text.toLowerCase().includes(q.toLowerCase()) ? (
    <Box component="span">{highlight(root.text, q)}</Box>
  ) : (
    <Box component="span">{root.text}</Box>
  );

  return (
    <Box
      onClick={() => onOpen(open ? null : root.queryId)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 46,
        gap: 0,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,.06)',
        bgcolor: open ? 'rgba(124,58,237,.03)' : 'transparent',
        '&:hover': { bgcolor: 'rgba(0,0,0,.02)' },
        flexShrink: 0,
      }}
    >
      {/* Axis rail */}
      <Box
        sx={{
          width: 6,
          minWidth: 6,
          alignSelf: 'stretch',
          bgcolor: axisColor,
          opacity: isPending ? 0.5 : 1,
          flexShrink: 0,
        }}
      />

      {/* Text + tags */}
      <Box sx={{ flex: 1, minWidth: 0, px: 1.5, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {onSelect && (
            <Box
              component="input"
              type="checkbox"
              checked={selected}
              onChange={() => onSelect(root.queryId)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Sélectionner" // i18n: promptStudio.batch.select
              sx={{ width: 12, height: 12, m: 0, flexShrink: 0, cursor: 'pointer', accentColor: '#7c3aed' }}
            />
          )}
          <Box
            sx={{
              fontSize: 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
              color: isPending ? 'text.secondary' : 'text.primary',
              fontStyle: isPending ? 'italic' : 'normal',
            }}
          >
            {textNode}
          </Box>
          <RowTags tags={root.tags} q={q} onTag={onTag} />
        </Box>

        {/* Expanded: PromptRowExpanded (step 11c) */}
        {open && brandId && (
          <PromptRowExpanded
            family={family}
            brandId={brandId}
            locations={locations}
            lang={lang}
            strategy={strategy}
            promptFigures={promptFigures}
            isAgency={isAgency}
            onFirstRun={onFirstRun}
            onChanged={onChanged}
            onClose={() => onOpen(null)}
          />
        )}
      </Box>

      {/* Lieu */}
      <Box sx={{ width: 168, minWidth: 168, flexShrink: 0, px: 0.5 }}>
        <LocChip
          zone={effectiveZone}
          legacyZone={!effectiveZone && place.row.zone ? place.row.zone : undefined}
        />
        {place.more > 0 && (
          <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', ml: 0.25 }}>
            +{place.more}
          </Box>
        )}
      </Box>

      {/* Langues */}
      <Box
        title={langs.map((l) => l.name).join(', ') || undefined}
        sx={{ width: 68, minWidth: 68, flexShrink: 0, fontSize: 12, color: 'text.secondary', px: 0.5, display: 'flex', alignItems: 'center', gap: 0.25 }}
      >
        {langs.map(({ code, name }) =>
          languageFlag(code)
            ? <Flag key={code} countryCode={languageFlag(code)} size={18} title={name} />
            : <span key={code}>{code.toUpperCase()}</span>,
        )}
      </Box>

      {/* Passage (LastRunCell) */}
      <LastRunCell run={lastRun} />

      {/* Prompts count */}
      <Box sx={{ width: 60, minWidth: 60, textAlign: 'right', fontSize: 12, color: 'text.secondary', pr: 0.5, flexShrink: 0 }}>
        {count !== null ? count : ''}
        {showFamilyToken && (
          <Box component="span" sx={{ ml: 0.5, opacity: 0.5, fontSize: 10 }}>
            {shape.holes > 0 ? '⬜' : '⬛'}
          </Box>
        )}
      </Box>

      {/* Tail */}
      <Box sx={{ width: 18, minWidth: 18, flexShrink: 0 }} />
    </Box>
  );
}

function highlight(text: string, q: string): React.ReactNode[] {
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.split(new RegExp(`(${escaped})`, 'gi')).map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
}

function languageName(code: string, uiLang: string): string {
  try {
    return new Intl.DisplayNames([uiLang || 'fr'], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

// PausedRow moved to ./PausedRow.tsx (step 11a).
// Re-export for backward compatibility with any existing importers.
export { PausedRow } from './PausedRow';
export type { PausedRowProps } from './PausedRow';
